/*
 * SNUGLER redesign: core. Shared helpers, the scroll loop, size/price sync, buttons and headings.
 * Section effects live in snugler-hero.js, snugler-loft.js, snugler-fit.js and snugler-sections.js,
 * which register themselves with SnuglerRedesign.register().
 * Vanilla JS, no dependencies. Every effect checks prefers-reduced-motion, pauses when off screen,
 * and re-initialises when a section reloads in the theme editor.
 * Add to cart is handled by Dawn's <product-form> (assets/product-form.js); this code only keeps
 * every size picker, price and hidden variant input on the page in sync.
 */
(() => {
  if (window.SnuglerRedesign) return;

  const SR = (window.SnuglerRedesign = { version: '1.0.0' });
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const isReduced = () => reduceQuery.matches;

  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
  const range = (v, a, b) => clamp((v - a) / (b - a));
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (t) => t * t * (3 - 2 * t);
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const easeBack = (t) => {
    const c1 = 1.5;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  };
  const fmt = (n) => Math.round(n * 10) / 10;

  /* ------------------------------------------------------------------ scroll loop */

  const scrollJobs = new Set();
  let scrollQueued = false;

  function runScrollJobs() {
    scrollQueued = false;
    const vh = window.innerHeight;
    scrollJobs.forEach((job) => {
      if (!job.el.isConnected) {
        scrollJobs.delete(job);
        return;
      }
      if (job.active) job.fn(vh);
    });
  }

  function requestScrollJobs() {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(runScrollJobs);
  }

  window.addEventListener('scroll', requestScrollJobs, { passive: true });
  window.addEventListener('resize', requestScrollJobs);

  const visibilityObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const job = entry.target.__snxJob;
              if (job) job.active = entry.isIntersecting;
            });
            requestScrollJobs();
          },
          { rootMargin: '25% 0px 25% 0px' }
        )
      : null;

  function onScroll(el, fn) {
    const job = { el, fn, active: !visibilityObserver };
    el.__snxJob = job;
    scrollJobs.add(job);
    if (visibilityObserver) visibilityObserver.observe(el);
    requestScrollJobs();
    return job;
  }

  function whenVisible(el, fn, options = {}) {
    if (!('IntersectionObserver' in window)) {
      fn();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            io.disconnect();
            fn();
          }
        });
      },
      { threshold: options.threshold ?? 0.25, rootMargin: options.rootMargin ?? '0px 0px -8% 0px' }
    );
    io.observe(el);
  }

  /* ------------------------------------------------------------------ money */

  let currency = 'USD';
  function money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  /* ------------------------------------------------------------------ variant store */

  const store = { variants: [], current: null };

  function loadVariants(root) {
    if (store.variants.length) return;
    const node = root.querySelector('[data-snx-variants]') || document.querySelector('[data-snx-variants]');
    if (!node) return;
    try {
      store.variants = JSON.parse(node.textContent);
      currency = node.dataset.currency || currency;
    } catch (e) {
      store.variants = [];
    }
    const checked = document.querySelector('[data-snx-variant-radio]:checked');
    const input = document.querySelector('[data-snx-variant-input]');
    const id = checked ? checked.value : input ? input.value : store.variants[0] && store.variants[0].id;
    store.current = findVariant(id) || store.variants[0] || null;
  }

  function findVariant(id) {
    return store.variants.find((v) => String(v.id) === String(id)) || null;
  }

  function selectVariant(id, source) {
    const variant = findVariant(id);
    if (!variant) return;
    const changed = !store.current || store.current.id !== variant.id;
    store.current = variant;
    applyVariant(variant, changed);
    if (changed) {
      document.dispatchEvent(new CustomEvent('snx:variant', { detail: { variant, source } }));
    }
  }
  SR.selectVariant = selectVariant;

  function flipText(el, text) {
    if (el.textContent.trim() === text) return;
    if (isReduced() || !el.animate) {
      el.textContent = text;
      return;
    }
    el.animate(
      [
        { transform: 'translateY(0)', opacity: 1 },
        { transform: 'translateY(-40%)', opacity: 0 },
      ],
      { duration: 160, easing: 'ease-in' }
    ).onfinish = () => {
      el.textContent = text;
      el.animate(
        [
          { transform: 'translateY(40%)', opacity: 0 },
          { transform: 'translateY(0)', opacity: 1 },
        ],
        { duration: 420, easing: 'cubic-bezier(.34,1.56,.64,1)' }
      );
    };
  }

  function applyVariant(variant, animate = true) {
    document.querySelectorAll('[data-snx-variant-input]').forEach((input) => {
      input.value = variant.id;
      input.disabled = !variant.available;
    });

    document.querySelectorAll('[data-snx-submit]').forEach((button) => {
      const label = button.querySelector('[data-snx-submit-label]');
      if (variant.available) {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
        if (label) label.textContent = button.dataset.addLabel || 'Add to cart';
      } else {
        button.setAttribute('disabled', 'disabled');
        if (label) label.textContent = button.dataset.soldoutLabel || 'Sold out';
      }
    });

    document.querySelectorAll('[data-snx-price]').forEach((el) => {
      const text = money(variant.price);
      if (animate) flipText(el, text);
      else el.textContent = text;
    });

    document.querySelectorAll('[data-snx-compare]').forEach((el) => {
      const show = variant.compare_at_price && variant.compare_at_price > variant.price;
      el.hidden = !show;
      if (show) el.textContent = money(variant.compare_at_price);
    });

    document.querySelectorAll('[data-snx-variant-radio]').forEach((radio) => {
      radio.checked = String(radio.value) === String(variant.id);
    });
    document.querySelectorAll('[data-snx-pills]').forEach(positionGlide);

    document.querySelectorAll('[data-snx-variant-select]').forEach((select) => {
      select.value = String(variant.id);
    });

    document.querySelectorAll('[data-snx-size-btn]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(String(btn.dataset.variantId) === String(variant.id)));
    });
  }

  /* ------------------------------------------------------------------ pills */

  function positionGlide(fieldset) {
    const glide = fieldset.querySelector('.snx-pills__glide');
    const checked = fieldset.querySelector('.snx-pills__input:checked');
    if (!glide || !checked) return;
    const label = fieldset.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
    if (!label) return;
    const track = glide.parentElement;
    const trackRect = track.getBoundingClientRect();
    const rect = label.getBoundingClientRect();
    if (!rect.width) return;
    glide.style.setProperty('--glide-x', `${rect.left - trackRect.left}px`);
    glide.style.setProperty('--glide-w', `${rect.width}px`);
    if (!fieldset.classList.contains('is-ready')) {
      glide.style.transition = 'none';
      fieldset.classList.add('is-ready');
      requestAnimationFrame(() => requestAnimationFrame(() => (glide.style.transition = '')));
    }
  }

  function initPills(root) {
    root.querySelectorAll('[data-snx-pills]').forEach((fieldset) => {
      if (fieldset.__snx) return;
      fieldset.__snx = true;
      fieldset.addEventListener('change', (event) => {
        const radio = event.target.closest('[data-snx-variant-radio]');
        if (radio) selectVariant(radio.value, fieldset);
      });
      positionGlide(fieldset);
      if ('ResizeObserver' in window) new ResizeObserver(() => positionGlide(fieldset)).observe(fieldset);
    });

    root.querySelectorAll('[data-snx-variant-select]').forEach((select) => {
      if (select.__snx) return;
      select.__snx = true;
      select.addEventListener('change', () => selectVariant(select.value, select));
    });
  }

  /* ------------------------------------------------------------------ buttons */

  function initButtons(root) {
    root.querySelectorAll('.snx-btn').forEach((button) => {
      if (button.__snx) return;
      button.__snx = true;

      button.addEventListener('pointerdown', (event) => {
        if (isReduced() || button.disabled) return;
        const rect = button.getBoundingClientRect();
        const ripple = document.createElement('span');
        ripple.className = 'snx-ripple';
        ripple.style.left = `${event.clientX - rect.left}px`;
        ripple.style.top = `${event.clientY - rect.top}px`;
        button.appendChild(ripple);
        ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
      });

      if (button.classList.contains('snx-btn--magnetic')) {
        button.addEventListener('pointermove', (event) => {
          if (isReduced() || !finePointer.matches) return;
          const rect = button.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          button.style.setProperty('--mx', `${fmt(x * 8)}px`);
          button.style.setProperty('--my', `${fmt(y * 6)}px`);
        });
        button.addEventListener('pointerleave', () => {
          button.style.setProperty('--mx', '0px');
          button.style.setProperty('--my', '0px');
        });
      }
    });

    root.querySelectorAll('[data-snx-qty-step]').forEach((stepper) => {
      if (stepper.__snx) return;
      stepper.__snx = true;
      stepper.addEventListener('click', () => {
        const input = stepper.parentElement.querySelector('[data-snx-qty-input]');
        if (!input) return;
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(stepper.dataset.snxQtyStep));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ------------------------------------------------------------------ fluff headings */

  function splitWords(el) {
    if (el.dataset.snxSplit) return;
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;
    el.dataset.snxSplit = '1';
    el.setAttribute('aria-label', text);
    el.textContent = '';
    text.split(' ').forEach((word, index, words) => {
      const outer = document.createElement('span');
      outer.className = 'snx-w';
      outer.setAttribute('aria-hidden', 'true');
      const inner = document.createElement('span');
      inner.className = 'snx-w__i';
      inner.style.setProperty('--i', index);
      inner.textContent = word;
      outer.appendChild(inner);
      el.appendChild(outer);
      if (index < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
  }

  function initFluff(root) {
    if (isReduced()) return;
    root.querySelectorAll('[data-snx-fluff]').forEach((el) => {
      if (el.__snx) return;
      el.__snx = true;
      splitWords(el);
      if (el.hasAttribute('data-snx-fluff-now')) {
        el.style.setProperty('--snx-fluff-delay', '180ms');
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-fluffed')));
      } else {
        whenVisible(el, () => el.classList.add('is-fluffed'), { threshold: 0.35 });
      }
    });
  }

  /* ------------------------------------------------------------------ modules + boot */

  SR.util = { isReduced, finePointer, clamp, range, lerp, smooth, easeOut, easeBack, fmt, onScroll, whenVisible, store, selectVariant, money };
  SR.modules = new Map();
  SR.booted = false;

  function runModule(fn, root) {
    try {
      fn(root);
    } catch (error) {
      console.error('[SNUGLER]', error);
    }
  }

  function init(root = document) {
    loadVariants(root);
    initPills(root);
    initButtons(root);
    initFluff(root);
    SR.modules.forEach((fn) => runModule(fn, root));
    if (store.current) applyVariant(store.current, false);
  }
  SR.init = init;

  SR.register = (name, fn) => {
    if (SR.modules.has(name)) return;
    SR.modules.set(name, fn);
    if (SR.booted) {
      runModule(fn, document);
      if (store.current) applyVariant(store.current, false);
    }
  };

  function boot() {
    if (SR.booted) return;
    SR.booted = true;
    init(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('load', () => document.querySelectorAll('[data-snx-pills]').forEach(positionGlide));
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => document.querySelectorAll('[data-snx-pills]').forEach(positionGlide));
})();